import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Divider,
    Layout,
    Row,
    Col,
    Space,
    Tabs,
    Tag,
    Typography
} from "antd";
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import { useLocation } from 'react-router-dom'; // Import useLocation
import SmaTickersTable from "./SmaTickersTable";
import SmaDrafts from "./SmaDrafts";
import TickerSearch from '../../components/TickerSearch/TickerSearch';
import PageHeader from "../../components/PageHeader";
import Placeholder from "../../components/Placeholder";
import {iconMapping} from "../../configs/AppsConfig";
import useSma from './useSma';
import SmaTemplates from "./SmaTemplates";
import SmaCharts from "./SmaCharts";
import {extractEtfsForSankey} from "./utils/smaUtils";
import NivoSankeyChart from "../../charts/nivo/NivoSankeyChart";
import TickerCoverage from "./TickerCoverage";
import CumulativeHoldingsTable from "./CumulativeHoldingsTable";
import formatCumulativeEtfHoldings from "./utils/formatCumulativeEtfHoldings";
import SectorTracking from "./SectorTracking";
import {calculateEtfContributions} from "./utils/smaUtils";
import SmaConfigurator from "./SmaConfigurator";

import {BubbleText} from "../../components/Text";
import WordWaterfall from "../../charts/WordWaterfall";
import DynamicLoader from "../../components/DynamicLoader/DynamicLoader";
import LoadingAnimation from "../../components/DynamicLoader/LoadingAnimation";

const SmaPreview = ({tickerData, etfColors, sectorColors, tickersDropped, setView})=>{


    const [isProcessing, setIsProcessing] = useState(true);


    return (
        <div>
            {isProcessing && (
                <LoadingAnimation words={tickersDropped.map(x=>x.symbol)} seconds={5} maxInteger={tickerData.length + tickersDropped.length} onFinish={()=>setIsProcessing(false)} />
            )}
            {!isProcessing && (
                <>
                    <div style={{textAlign: 'center'}} className={'p-6'}>
                        <BubbleText text={`${tickerData.length} STOCKS KEPT`} />
                    </div>
                    <Row justify={'center'}>
                        <Space>
                            <ButtonGroup>
                                <Button>Explain Results</Button>
                            </ButtonGroup>
                            <Button variant={'contained'} color={'primary'} onClick={()=>setView('table')}>
                                Edit Now
                            </Button>

                        </Space>
                    </Row>
                    <Divider/>
                    <Row justify={'center'}>
                        <Col xs={24} xl={22} xxl={20}>
                            {tickerData && <SmaTickersTable data={tickerData}
                                                            etfColors={etfColors}
                                                            sectorColors={sectorColors}
                                                            logoCloudCount={15}
                                                            hideExport={true}
                            />}

                        </Col>
                    </Row>
                </>
            )}
        </div>
    )


}


const Sma = () => {
    const location = useLocation(); // Use the useLocation hook to get the current location
    const [view, setView] = useState('templates');
    const isPublic = useSelector(state=>state.public.isPublicAccess)

    const {
        addEtfs, removeEtf, etfColors, etfData,
        sectorColors, mergedData, filledHoldings, summaryDetails,
        currentSmaConfig, updateConfig, updateEtfWeight, toggleReplicateEtf,
        blacklists, screens,
        sectorsBarData, sectorsBarChartField, setSectorsBarChartField,
        saveSma, getSectorTickers, allSectors,
        replicateKey,
        sectorRemainders, sectorWeightChanges
    } = useSma();

    const etfContributions = etfData && filledHoldings && calculateEtfContributions(filledHoldings, etfData);


    const onAddEtfs = (etfs)=>{
        addEtfs(etfs);
        setView('table');
    }

    const getReplicatedEtfsData = ()=>{
        if (Object.keys(combinedEtfData).length === 0) return null;
        return Object.keys(combinedEtfData).filter(x=>!combinedEtfData[x].doNotReplicate).map(x=>({symbol: x, ...combinedEtfData[x]}));
    }


    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const viewParam = queryParams.get('view');
        if (viewParam) {
            setView(viewParam);
        }
    }, [location.search]); // Re-run the effect if the search string changes


    const combinedEtfData = {};
    Object.keys(etfData).forEach((key) => {
      combinedEtfData[key] = { ...etfData[key], ...currentSmaConfig?.etfs?.[key] };
    });

    console.log("ETF Data", etfData)

    // if (!(Object.values(etfData).some(x=>x!==null))) {
    //     return (
    //         <div>empty</div>
    //     )
    // }

    return (
        <Layout>
            {view !== 'preview' && (
                <PageHeader title={'SMA Builder'} logoUrl={iconMapping.smalab}
                            actions={[
                                view==='search' ? <Button color={'error'} variant={'outlined'} onClick={()=>setView('table')}>Cancel Search</Button> : <Button onClick={()=>setView('search')}>Search ETFs</Button> ,
                                <Button onClick={()=>setView('templates')}>View Templates</Button>,
                                <Button onClick={()=>setView('drafts')}>View Drafts</Button>
                            ]}
                />
            )}
            {view === 'preview' && Array.isArray(mergedData) &&(
                      <SmaPreview tickerData={filledHoldings}
                                  etfColors={etfColors}
                                  sectorColors={sectorColors}
                                  tickersDropped={[...mergedData.filter(x=>!filledHoldings.map(x=>x.symbol).includes(x.symbol))]}
                                  setView={setView}

                      />
            )}
            <Layout.Content style={{padding: '2.5%'}}>
                {
                    view === 'search' && (
                        <TickerSearch
                              maxTickers={5}
                              types={['etf']}
                              onTickersSubmit={(tickers)=>onAddEtfs(tickers.map(x=>x.symbol))}
                        />
                    )
                }
                {
                    view === 'templates' && (
                        <SmaTemplates onSubmit={()=>setView('table')} onCreate={()=>setView('search')}/>
                    )
                }
                {
                    view === 'drafts' && (
                        <SmaDrafts onSubmit={()=>setView('table')} />
                    )
                }
                {
                    view === 'table' && (
                        mergedData ?
                            <Tabs defaultActiveKey={'1'} tabPosition={'right'}>
                                <Tabs.TabPane key={'1'} tab={'Preview'}>
                                    <SmaConfigurator etfData={combinedEtfData}
                                                  tickerData={filledHoldings}
                                                  fullData={mergedData}
                                                  etfColors={etfColors}
                                                  sectorColors={sectorColors}
                                                  config={currentSmaConfig}
                                                  blacklists={blacklists}
                                                  screens={screens}
                                                  summaryDetails={{...summaryDetails, sectorRemainders, sectorWeightChanges}}
                                                  callbacks={{
                                                      removeEtf, toggleReplicateEtf,
                                                      updateConfig, updateEtfWeight,
                                                      saveSma
                                                  }}
                                                  etfContributions={etfContributions}



                                    />
                                </Tabs.TabPane>
                                <Tabs.TabPane key={'1b'} tab={'ETF Fills'}>
                                    <CumulativeHoldingsTable detailedRows={formatCumulativeEtfHoldings(getReplicatedEtfsData())} />
                                </Tabs.TabPane>
                                <Tabs.TabPane key={'2'} tab={'Charts'}>
                                    <SmaCharts sectorsBarChartField={sectorsBarChartField}
                                               setSectorsBarChartField={setSectorsBarChartField}
                                               sectorsBarData={sectorsBarData}
                                               summaryDetails={summaryDetails}
                                               cutTickerData={filledHoldings}
                                               fullTickerData={mergedData}
                                               key={replicateKey}
                                    />
                                </Tabs.TabPane>
                                <Tabs.TabPane key={'3'} tab={'Coverage'}>
                                    <TickerCoverage condensedTickers={filledHoldings} fullTickers={mergedData}/>
                                </Tabs.TabPane>
                                {!isPublic && (
                                    <Tabs.TabPane key={'5'} tab={'Sector Tracking'}>
                                        <SectorTracking allSectors={allSectors} getSectorTickers={getSectorTickers}/>
                                    </Tabs.TabPane>
                                )}
                            </Tabs>
                            :
                            <Placeholder imgUrl={'https://storage.googleapis.com/finiat-web-static-resources/appIcons/cube-grid.jpg'}
                                         text={"No SMA Currently In Progress"}
                                         action={<Button variant={'contained'} onClick={()=>setView('templates')}>Start Using an SMA Now</Button>}
                            />
                    )
                }
            </Layout.Content>

        </Layout>
    )
}

export default Sma;